import { alpha, createTheme, type PaletteMode, type ThemeOptions } from '@mui/material/styles';

import buildPalette, { INVYO_INK } from './palette';
import typography from './typography';

export { INVYO_GOLD, INVYO_INK, INVYO_MAGENTA, INVYO_TEAL } from './palette';
export { bodyFontFamily, displayFontFamily } from './typography';

const shape = { borderRadius: 12 };

/**
 * A fourth Button variant, beside MUI's text / outlined / contained.
 *
 * `soft` is one shape (a low-contrast fill and a hairline edge) that carries
 * its meaning in colour rather than in weight. It reads as a button without
 * claiming the moment, which is what the three stock variants cannot do here:
 * `text` is indistinguishable from a label, `outlined` draws its border from
 * `currentColor` (so a neutral one and a red one end up with edges of visibly
 * different weight, and the brighter reads as the bigger button), and
 * `contained` is the one thing a screen should have at most one of.
 *
 * It honours `color`, as every stock variant does; a bar of soft buttons is
 * ranked by hue, not by four different shapes:
 *
 * - `color="inherit"`: neutral. Controls that are always there: columns, export.
 * - `color="primary"` (Button's default): the action of the moment.
 * - `color="warning"`: consequential but reversible: archiving, suspending.
 * - `color="success"`: restoring what a warning action took away.
 * - `color="error"`: destructive.
 *
 * The edge is the same hairline in all five. The red in particular never
 * reaches it: a destructive action should state its cost in the fill and the
 * label, where it warns, not in the outline, where it attracts.
 *
 * "Honours `color`" is a claim this file has to keep. It did not: warning and
 * success had no entry, so asking for them returned the shared shape (a border
 * and no tint) and the button read as neutral. `button-soft.test.tsx` now walks
 * every tone rather than the three that happened to exist.
 */
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    soft: true;
  }
}

/**
 * The same fourth variant on Alert, beside MUI's standard / filled / outlined.
 *
 * Same shape as the button's (a low-contrast fill and a hairline edge) for the
 * same reason: an alert inside a card should not add a second frame of its own,
 * and it should not shout louder than the card it sits in.
 *
 * What it fixes over `standard`, which is the closest stock variant: MUI builds
 * that one by running `lighten`/`darken` on the severity colour at fixed ratios,
 * so the ink is computed rather than chosen. On our light palette that lands the
 * gold warning at 1.54:1 against its own tint; the message is gone. `soft`
 * takes its ink from `palette.softInk`, where the four values are measured
 * (see the doc-comment there) and asserted in `alert-soft.test.tsx`.
 *
 * Ranked by `severity`, exactly as the button is ranked by `color`. The edge
 * carries the severity too (its hue at the card border's alpha) because a grey
 * line around a coloured fill reads as a frame someone forgot to remove. What
 * stays shared is the weight: one hairline for all four, so an error states its
 * cost in the fill and the words, not in a heavier outline.
 */
declare module '@mui/material/Alert' {
  interface AlertPropsVariantOverrides {
    soft: true;
  }
}

/**
 * And on Chip, beside MUI's filled / outlined.
 *
 * A chip in a dense table has to be readable at a glance without becoming the
 * loudest thing on the row. `filled` is the loudest thing on the row: a solid
 * block of hue behind white text. `outlined` is the opposite failure: on a table
 * that already draws a line under every row, one more rectangle of hairlines
 * reads as chrome, and the colour survives only in the border, which is where
 * the eye looks last.
 *
 * `soft` is the same low-contrast fill as the button and the alert, and takes
 * its ink from `palette.softInk` for the same measured reason. It carries NO
 * border, and that is the one place it departs from its two siblings: they are
 * targets and need an edge to read as one, a chip is a label. Adding the
 * hairline back would put the outline exactly where it was rejected.
 */
declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    soft: true;
  }
}

/* ─── Helper: build theme for a given mode ──────────────────────── */
function buildTheme(mode: PaletteMode) {
  // Colour lives in `palette.ts`. The base theme is built first so the
  // component overrides below read their values back off it, rather than
  // keeping a parallel set of local aliases.
  const base = createTheme({ palette: buildPalette(mode), typography, shape });
  const { palette } = base;
  const isDark = mode === 'dark';
  const BG_CARD = palette.surface.card;
  const NEUTRAL = palette.grey[500];
  const TEXT_PRIMARY = palette.text.primary;
  const TEXT_SECONDARY = palette.text.secondary;

  /**
   * One tone of the `soft` button: what the label is made of, and what it sits on.
   *
   * The label and the fill are separate arguments rather than one hue at two
   * opacities, because for the brand teal they are not the same colour. Light
   * cannot take `primary.main` as ink; `palette.ts` measures the brand teal at
   * 1.36:1 on its own tint and calls it gone, which is exactly how the selection
   * bar came out: a pale mint "Restore" on a pale mint band. The ink darkens with
   * the mode (`accent.client`), the tint does not. Same split the side nav
   * already uses for its active item.
   *
   * The edge takes the tone's own hue at one shared alpha: the recipe `MuiAlert`
   * already used: a neutral grey line around a tinted fill read as a leftover
   * frame, not as part of the shape. The weight and the alpha stay shared, so no
   * tone gives itself a louder edge than its neighbours; only the hue changes.
   * `color="inherit"` passes nothing and keeps the neutral `divider`.
   */
  const softEdge = (hue: string) => `1px solid ${alpha(hue, isDark ? 0.24 : 0.3)}`;

  const softTone = (
    label: string,
    fill: string,
    hover: string,
    fontWeight: number,
    edge?: string,
  ) => ({
    fontWeight,
    color: label,
    backgroundColor: fill,
    ...(edge ? { border: softEdge(edge) } : {}),
    '&:hover': { backgroundColor: hover },
  });

  // Typed here rather than inline: `createTheme(base, …)` types its extra
  // arguments as plain `object`, which would leave the callbacks' `theme` any.
  const components: ThemeOptions['components'] = {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontSize: '16px',
          '@media (max-width: 1600px)': {
            fontSize: '14px',
          },
          '@media (max-width: 1280px)': {
            fontSize: '13px',
          },
        },
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: `${alpha(NEUTRAL, isDark ? 0.2 : 0.25)} transparent`,
        },
        '::selection': {
          background: alpha(palette.primary.main, 0.25),
        },
        /*
         * The focus ring is NOT here, and that is the point.
         *
         * A rule in CssBaseline is resolved once, against whatever theme wraps
         * the root: the shell, which is always dark. The content below it runs
         * in a nested ThemeProvider that can be light. A global rule would have
         * painted the dark #00E0DA ring across light pages, where it manages
         * 1.53:1: invisible, on the one indicator that cannot afford to be. It
         * lives on the components instead, where it resolves against the theme
         * actually in force.
         */
        /* Hide number input spinners globally */
        'input[type=number]::-webkit-outer-spin-button, input[type=number]::-webkit-inner-spin-button':
          {
            WebkitAppearance: 'none',
            margin: 0,
          },
        'input[type=number]': {
          MozAppearance: 'textfield',
        },
      },
    },
    /*
     * Keyboard focus, for everything that takes it. Before this the app had no
     * focus rule at all and leaned on MUI's focus ripple, a mood, not an
     * indicator.
     *
     * `outline-offset` does the real work. A ring that touches what it surrounds
     * must contrast with BOTH the backdrop and the component, and no colour
     * manages that: a teal ring on the teal primary button is 1.00:1, literally
     * the same colour. The 2px gap is transparent, so it shows whatever is
     * really behind; the ring only ever has to clear the surface, and the
     * impossible constraint is gone by construction.
     *
     * `:focus-visible`, not `:focus`: the ring is for the keyboard, and a mouse
     * click should not paint one.
     *
     * Resolved at render (`({ theme: t })`) so a light page nested under the
     * dark shell gets the light ring.
     */
    MuiButtonBase: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          '&:focus-visible': {
            outline: `2px solid ${t.palette.focusRing}`,
            outlineOffset: 2,
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 24px',
          fontSize: '0.8125rem',
          transition: 'all 0.2s ease',
        },
      },
      // See the `soft` declaration at the top of this file for what it is for.
      // Order matters: variants apply in the order listed, so the shared shape
      // comes first and each colour recolours it.
      variants: [
        {
          props: { variant: 'soft' },
          style: {
            border: `1px solid ${palette.divider}`,
            '&.Mui-disabled': { border: `1px solid ${palette.divider}` },
          },
        },
        {
          props: { variant: 'soft', color: 'inherit' },
          style: softTone(
            TEXT_SECONDARY,
            alpha(NEUTRAL, isDark ? 0.12 : 0.1),
            alpha(NEUTRAL, isDark ? 0.2 : 0.16),
            500,
          ),
        },
        {
          props: { variant: 'soft', color: 'primary' },
          // The accent pair, not a hand-mixed alpha of the brand teal: `bg` and
          // `hover` are the tint recipe `palette.ts` keeps identical across modes,
          // and `main` is the ink that had to move for light.
          style: softTone(
            palette.accent.client.main,
            palette.accent.client.bg,
            palette.accent.client.hover,
            600,
            palette.accent.client.main,
          ),
        },
        {
          props: { variant: 'soft', color: 'error' },
          // `error.main` is already stated per mode in the palette, so the ink
          // needs no correction here; only the teal did.
          style: softTone(
            palette.error.main,
            alpha(palette.error.main, isDark ? 0.12 : 0.1),
            alpha(palette.error.main, isDark ? 0.2 : 0.16),
            600,
            palette.error.main,
          ),
        },
        // Warning and success take their ink from `palette.softInk`, not from
        // `.main`. That is the whole reason `softInk` exists: a hue laid on its
        // own tint is the narrowest contrast in the palette, and gold in
        // particular collapses to 1.54:1 in light; the doc-comment on `SOFT_INK`
        // has the table. `MuiAlert` below already reads those four values; the
        // Button had only three tones and simply had no entry for these two, so
        // asking for them produced the shared shape with MUI's default text
        // colouring: a button with a border and no tint at all.
        {
          props: { variant: 'soft', color: 'warning' },
          style: softTone(
            palette.softInk.warning,
            alpha(palette.warning.main, isDark ? 0.12 : 0.1),
            alpha(palette.warning.main, isDark ? 0.2 : 0.16),
            600,
            palette.warning.main,
          ),
        },
        {
          props: { variant: 'soft', color: 'success' },
          style: softTone(
            palette.softInk.success,
            alpha(palette.success.main, isDark ? 0.12 : 0.1),
            alpha(palette.success.main, isDark ? 0.2 : 0.16),
            600,
            palette.success.main,
          ),
        },
        // Material UI 9 dropped the colour-specific keys (containedPrimary,
        // outlinedPrimary): the primary button's gradient lives in variants now.
        // Resolved at render, not at build, so the swatch is read from whatever
        // theme is actually in force rather than baked in once.
        {
          props: { variant: 'contained', color: 'primary' },
          style: ({ theme: t }) => ({
            background: `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${alpha(t.palette.primary.main, 0.85)} 100%)`,
            boxShadow: `0 2px 12px ${alpha(t.palette.primary.main, 0.2)}`,
            '&:hover': {
              background: `linear-gradient(135deg, ${t.palette.primary.main} 20%, ${alpha(t.palette.primary.main, 0.95)} 100%)`,
              boxShadow: `0 4px 20px ${alpha(t.palette.primary.main, 0.35)}`,
              transform: 'translateY(-1px)',
            },
            '&:active': { transform: 'translateY(0)' },
            // MUI's disabled state only resets background-COLOR, so the gradient
            // (a background-image) and the glow survived it: the label went grey
            // while the button stayed teal. MUI never sets background-image, so
            // clearing it here can't lose a specificity fight.
            '&.Mui-disabled': {
              backgroundImage: 'none',
              boxShadow: 'none',
              transform: 'none',
            },
          }),
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: ({ theme: t }) => ({
            borderColor: alpha(t.palette.primary.main, 0.4),
            '&:hover': {
              borderColor: t.palette.primary.main,
              backgroundColor: alpha(t.palette.primary.main, 0.06),
            },
          }),
        },
      ],
    },
    // See the `soft` declaration at the top of this file. Same ordering rule as
    // the button: the shared shape first, then one entry per severity.
    MuiAlert: {
      variants: [
        {
          props: { variant: 'soft' },
          style: {
            border: `1px solid ${palette.divider}`,
            borderRadius: shape.borderRadius,
            // The icon inherits the ink; MUI dims it to 0.9 by default, which
            // reads as a second, quieter colour next to the text it belongs to.
            '& .MuiAlert-icon': { color: 'inherit', opacity: 1 },
          },
        },
        ...(['success', 'info', 'warning', 'error'] as const).map((severity) => ({
          props: { variant: 'soft' as const, severity },
          style: {
            color: palette.softInk[severity],
            backgroundColor: alpha(palette[severity].main, isDark ? 0.12 : 0.1),
            // The edge takes the severity's hue at the card border's own alpha
            // (`surface.cardBorder`, same ratio per mode). A neutral grey line
            // around an ambre fill read as a leftover frame; the tint belongs to
            // the whole shape, edge included. The weight stays shared, so no
            // severity gets a louder outline than its neighbours.
            border: `1px solid ${alpha(palette[severity].main, isDark ? 0.24 : 0.3)}`,
          },
        })),
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: BG_CARD,
          // The pair, not a hand-mixed local alpha. This border was 0.06:
          // 1.08:1, which draws no line at all. The card was still perfectly
          // visible, because its fill carries it; what was missing was a
          // defined edge rather than a soft fade.
          border: `1px solid ${palette.surface.cardBorder}`,
          // Light gets a real shadow: it is the only thing that can lift white
          // off near-white. Dark keeps a token one: a black glow on #080C14 has
          // nowhere to land, so the edge is the border's job there.
          boxShadow: isDark
            ? `0 2px 8px ${alpha('#000', 0.2)}, 0 0 1px ${alpha('#000', 0.1)}`
            : `0 2px 8px ${alpha(INVYO_INK, 0.07)}, 0 8px 24px -4px ${alpha(INVYO_INK, 0.1)}`,
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    // Links are not ButtonBase, so they need saying separately.
    MuiLink: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          '&:focus-visible': {
            outline: `2px solid ${t.palette.focusRing}`,
            outlineOffset: 2,
            borderRadius: 2,
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
            {
              WebkitBoxShadow: `0 0 0 100px ${BG_CARD} inset !important`,
              WebkitTextFillColor: `${TEXT_PRIMARY} !important`,
              caretColor: TEXT_PRIMARY,
              borderRadius: 'inherit',
              transition: 'background-color 5000s ease-in-out 0s',
            },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: 10,
          transition: 'box-shadow 0.2s ease',
          '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${alpha(t.palette.primary.main, 0.12)}`,
          },
          // A field's focus is carried by its own border, so it answers to the
          // same contract as the ring. MUI's default paints it `primary.main`,
          // which is 1.66:1 on a white field; the glow above is decoration, and
          // decoration was all that was left.
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: t.palette.focusRing,
          },
        }),
        input: {
          '&:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 100px ${BG_CARD} inset !important`,
            WebkitTextFillColor: `${TEXT_PRIMARY} !important`,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(NEUTRAL, 0.06),
          borderRadius: 10,
          '&:hover': { backgroundColor: alpha(NEUTRAL, 0.1) },
          '&.Mui-focused': { backgroundColor: alpha(NEUTRAL, 0.1) },
        },
        input: {
          '&:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 100px ${BG_CARD} inset !important`,
            WebkitTextFillColor: `${TEXT_PRIMARY} !important`,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: BG_CARD,
          border: `1px solid ${alpha(NEUTRAL, isDark ? 0.1 : 0.15)}`,
          boxShadow: isDark
            ? `0 8px 24px ${alpha('#000', 0.3)}`
            : `0 8px 24px ${alpha(NEUTRAL, 0.12)}`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: alpha(NEUTRAL, isDark ? 0.06 : 0.12) },
        head: {
          fontWeight: 600,
          color: TEXT_SECONDARY,
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, borderRadius: 8, letterSpacing: '0.01em' },
      },
      // See the `soft` declaration at the top of this file. Same ordering rule as
      // the button and the alert, but no shared shape entry here, because the
      // shape a chip shares with its siblings is precisely the border it must
      // not have.
      //
      // Every colour gets an entry. That is not thoroughness for its own sake:
      // the Button shipped with three tones and no entry for warning or success,
      // so asking for them returned the bare shape and the tint silently
      // vanished. A variant that claims to honour `color` has to answer for all
      // of them, and `chip-soft.test.tsx` walks the list.
      variants: [
        {
          props: { variant: 'soft', color: 'default' },
          style: {
            color: TEXT_SECONDARY,
            backgroundColor: alpha(NEUTRAL, isDark ? 0.12 : 0.1),
          },
        },
        {
          props: { variant: 'soft', color: 'primary' },
          // The accent pair, not a hand-mixed alpha: the brand teal cannot be its
          // own ink in light (1.36:1 on its own tint), so `accent.client` keeps
          // the tint fixed across modes and moves only the label.
          style: {
            color: palette.accent.client.main,
            backgroundColor: palette.accent.client.bg,
          },
        },
        // `secondary` rides with the four severities: it was the one chip colour
        // the table left out, and a status chip asking for it got no tint (PI-108).
        ...(['success', 'info', 'warning', 'error', 'secondary'] as const).map((color) => ({
          props: { variant: 'soft' as const, color },
          style: {
            color: palette.softInk[color],
            backgroundColor: alpha(palette[color].main, isDark ? 0.12 : 0.1),
          },
        })),
      ],
    },
    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme: t }) => ({
          height: 2,
          borderRadius: 1,
          backgroundColor: t.palette.primary.main,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
          minHeight: 48,
          transition: 'color 0.15s ease',
          '&.Mui-selected': { fontWeight: 600 },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.surface.elevated,
          color: TEXT_PRIMARY,
          border: `1px solid ${alpha(NEUTRAL, 0.12)}`,
          fontSize: '0.75rem',
          borderRadius: 8,
          boxShadow: isDark
            ? `0 4px 16px ${alpha('#000', 0.3)}`
            : `0 4px 16px ${alpha(NEUTRAL, 0.12)}`,
        },
        arrow: {
          color: palette.surface.elevated,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: BG_CARD,
          border: `1px solid ${alpha(NEUTRAL, isDark ? 0.1 : 0.12)}`,
          boxShadow: isDark
            ? `0 8px 32px ${alpha('#000', 0.4)}`
            : `0 8px 32px ${alpha(NEUTRAL, 0.12)}`,
          borderRadius: 12,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { transition: 'background-color 0.15s ease, color 0.15s ease' },
      },
    },
  };

  return createTheme(base, { components });
}

/* ─── Exports ───────────────────────────────────────────────────── */

/** Dark theme: always used for the shell (TopBar + SideNav). */
const shellTheme = buildTheme('dark');

/** Create the content theme for a given mode. */
export function createContentTheme(mode: PaletteMode) {
  return buildTheme(mode);
}

/*
 * There is no `createAdminTheme` any more, and that is the change.
 *
 * It swapped `palette.primary` for #a855f7, so every button in /admin turned
 * violet. Repainting the actions is the one place the signal does nothing: at
 * the moment you act, what protects you is a confirmation, not a hue. Meanwhile
 * the section was already unmistakable (the side nav serves entirely different
 * items there), so the violet was a second coat over a signal that already
 * existed, and it cost a whole parallel theme to apply.
 *
 * It also cost 30° of the colour wheel, reserved so charts would not collide
 * with it. `dataviz.categorical` now spends that slot on its 268° anchor.
 *
 * Admin keeps a colour, in the chrome only, and it is amber: the brand gold,
 * not a new hue. It reads better than the violet did on the same surfaces
 * (11.36:1 against #0F1420, where the violet managed 4.65:1), and it says the
 * true thing: this is a context to be careful in, not a different product.
 */

export default shellTheme;
